// src/hooks/useVnb.js
// Hooks fuer VNB-Stammdaten (Verteilnetzbetreiber).

import { useState, useEffect, useCallback } from 'react';
import { db } from '../lib/supabase';

// Liste aller VNBs (mit Suche + Pagination)
export function useVnben({ search = '', pageSize = 100 } = {}) {
  const [vnben, setVnben] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { setPage(0); }, [search, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, count: total, error: err } = await db.vnb.list({
        search, limit: pageSize, offset: page * pageSize,
      });
      if (err) throw err;
      setVnben(data || []);
      setCount(total || 0);
    } catch (e) {
      setError(e.message || 'Fehler beim Laden der VNBs');
      setVnben([]);
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  return {
    vnben,
    count,
    loading,
    error,
    page,
    setPage,
    totalPages: Math.max(1, Math.ceil(count / pageSize)),
    hasNext: page < Math.ceil(count / pageSize) - 1,
    hasPrev: page > 0,
    reload: load,
  };
}

// Einfache Liste (alle aktiven, fuer Dropdowns)
export function useVnbListe() {
  const [vnben, setVnben] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await db.vnb.listAll();
      if (err) throw err;
      setVnben(data || []);
    } catch (e) {
      setError(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { vnben, loading, error, reload: load };
}

// Einzelner VNB
export function useVnb(vnbId) {
  const [vnb, setVnb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!vnbId) { setVnb(null); setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error: err } = await db.vnb.get(vnbId);
      if (err) throw err;
      setVnb(data);
    } catch (e) {
      setError(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [vnbId]);

  useEffect(() => { load(); }, [load]);

  return { vnb, loading, error, reload: load };
}
